import { PageTitle as SharedPageTitle } from '../../components/layout/PageHeader';
/**
 * AccountLinkingPage - Handles account linking consent flow
 * Shows when user tries to OAuth login but account already exists
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button } from '@components/ui/Button';
import { LoadingSpinner } from '@components/ui/LoadingSpinner';
import { Icon } from '@components/ui/Icon';
import { apiService } from '@services/api';

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  width: 100%;
  min-width: 0;
  padding: 0;
`;

const SecurityIcon = styled.div`
  display: flex;
  margin-bottom: 1rem;
  color: var(--brand-600);
  svg {
    width: 24px;
    height: 24px;
  }
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const AccountInfo = styled.div`
  background: var(--neutral-25);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const AccountDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  font-weight: 500;
  color: var(--text-primary);
`;

const Value = styled.span`
  color: var(--text-muted);
  font-family: monospace;
  font-size: 0.9rem;
`;

const SecurityWarning = styled.div`
  background: var(--warning-50);
  border: 1px solid var(--warning-100);
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-bottom: 2rem;

  h3 {
    color: var(--warning-600);
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
  }

  p {
    color: var(--warning-600);
    font-size: 0.9rem;
    line-height: 1.4;
    margin: 0;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SecondaryActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const ErrorMessage = styled.div`
  background: var(--danger-50);
  border: 1px solid var(--danger-100);
  border-radius: var(--radius-md);
  padding: 1rem;
  color: var(--danger-600);
  margin-bottom: 1rem;
  text-align: center;
`;

// ============================================================================
// Component
// ============================================================================

export const AccountLinkingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = searchParams.get('email');
  const maskedEmail = useMemo(() => {
    if (!email) return null;
    const [local, domain] = email.split('@');
    if (!local || local.length < 2) {
      return `***@${domain ?? '***'}`;
    }
    return `${local[0]}***@${domain ?? '***'}`;
  }, [email]);

  const provider = searchParams.get('provider');
  const sub = searchParams.get('sub');

  useEffect(() => {
    void apiService.initialize();
  }, []);

  useEffect(() => {
    // Validate required parameters
    if (!email || !provider || !sub) {
      navigate('/auth/login', {
        replace: true,
        state: { error: 'Parámetros de vinculación inválidos' },
      });
    }
  }, [email, provider, sub, navigate]);

  const handleLinkAccounts = async () => {
    if (!email || !provider || !sub) return;

    setIsLoading(true);
    setError(null);

    try {
      // First, request account linking
      const linkingResponse = await apiService.requestAccountLinking({
        provider,
        provider_subject: sub,
        provider_email: email,
      });

      // Get the linking token from response
      const { token_hint: tokenHint, expires_in_minutes: expiresInMinutes } =
        linkingResponse.data;

      if (!tokenHint) {
        throw new Error(
          'No se pudo generar la referencia de vinculación. Contacta al soporte si el problema persiste.',
        );
      }

      toast(
        expiresInMinutes
          ? `Revisa tu correo e introduce el token de vinculación antes de ${expiresInMinutes} minutos.`
          : 'Revisa tu correo para obtener el token de vinculación.',
      );

      navigate('/auth/link/confirm', {
        state: {
          provider,
          tokenHint,
          email,
        },
        replace: true,
      });

      toast.success(
        'Solicitud enviada. Sigue las instrucciones del correo para completar la vinculación.',
      );
    } catch (linkingError: unknown) {
      const apiError = linkingError as {
        status?: number;
        detail?: string;
        message?: string;
      };

      if (apiError.status === 409) {
        setError(
          'Esta cuenta ya está vinculada a otro usuario. Usa una cuenta de Google diferente.',
        );
      } else if (apiError.status === 403) {
        setError('Debes verificar tu email antes de vincular cuentas.');
      } else {
        setError(
          apiError.detail || 'Error al vincular cuentas. Intenta de nuevo.',
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/auth/login', { replace: true });
  };

  const handleUseExistingAccount = () => {
    navigate('/auth/login', {
      replace: true,
      state: {
        prefillEmail: email,
        message: 'Inicia sesión con tu contraseña existente',
      },
    });
  };

  if (!email || !provider || !sub) {
    return <LoadingSpinner message="Validando parámetros..." />;
  }

  return (
    <Container>
      <SecurityIcon>
        <Icon name="link" size="xl" aria-hidden />
      </SecurityIcon>

      <Title>Vincular cuentas</Title>
      <Subtitle>
        Detectamos que ya tienes una cuenta con este email. ¿Deseas vincular tu
        cuenta de Google con tu cuenta existente?
      </Subtitle>

      <AccountInfo>
        <AccountDetail>
          <Label>Email:</Label>
          <Value>{maskedEmail}</Value>
        </AccountDetail>
        <AccountDetail>
          <Label>Cuenta existente:</Label>
          <Value>Email + Contraseña</Value>
        </AccountDetail>
        <AccountDetail>
          <Label>Nueva vinculación:</Label>
          <Value>Google OAuth</Value>
        </AccountDetail>
      </AccountInfo>

      <SecurityWarning>
        <h3>
          <Icon name="warning" size="sm" aria-hidden /> Nota de Seguridad
        </h3>
        <p>
          Solo vincula cuentas si controlas ambas. Una vez vinculadas, podrás
          acceder con cualquiera de los dos métodos de autenticación.
        </p>
      </SecurityWarning>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Actions>
        <Button
          variant="primary"
          size="md"
          fullWidth
          onClick={handleLinkAccounts}
          loading={isLoading}
          disabled={isLoading}
        >
          <Icon name="link" size="sm" aria-hidden /> Sí, vincular mis cuentas
        </Button>

        <Button
          variant="secondary"
          size="md"
          fullWidth
          onClick={handleUseExistingAccount}
          disabled={isLoading}
        >
          🔑 Usar cuenta existente (contraseña)
        </Button>
      </Actions>

      <SecondaryActions>
        <Link to="/auth/register">
          <Button variant="tertiary" size="sm">
            Crear cuenta nueva
          </Button>
        </Link>

        <Button
          variant="tertiary"
          size="sm"
          onClick={handleCancel}
          disabled={isLoading}
        >
          Cancelar
        </Button>
      </SecondaryActions>
    </Container>
  );
};

export default AccountLinkingPage;
